/**
 * ============================================================================
 * NewDocumentForm — department-routed authoring (V18).
 *
 * Owner: Phase 7+ — Department hierarchy.
 *
 * NEW UX (per drafts/51_department_hierarchy_model.md):
 *   1. Choose a doc type (SOP / WI / BMR / …)
 *   2. Pick the department this doc belongs to.
 *      The form filters templates to that department + its parents.
 *   3. (Optional) Pick an approved template — pre-fills title, owning dept,
 *      and seeds the source artefact.
 *   4. (Optional) Upload your own SOP file (.docx / .pdf) — overrides the
 *      template artefact.
 *   5. Enter title + submit.
 *
 *   The doc is created via POST /api/v1/documents/upload with department_id
 *   + template_id + source_artifact (base64). The next StartWorkflowModal
 *   derives reviewers from the department hierarchy automatically.
 *
 * URS rows: WF-031..038 (NEW).
 * ============================================================================
 */

import { useEffect, useMemo, useRef, useState, type FormEvent, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import { apiFetch } from '../../api/client';
import { useDepartments, useSopTemplates, useReviewerChain } from '../../api/orgHooks';
import type { CreateDocumentResponse } from '../../api/hooks';
import styles from './NewDocumentForm.module.css';

export const DOC_TYPE_CODES = [
  { code: 'SOP',  labelKey: 'newDocument.docTypes.sop' },
  { code: 'WI',   labelKey: 'newDocument.docTypes.wi' },
  { code: 'POL',  labelKey: 'newDocument.docTypes.policy' },
  { code: 'BMR',  labelKey: 'newDocument.docTypes.bmr' },
  { code: 'PRC',  labelKey: 'newDocument.docTypes.protocol' },
  { code: 'SPEC', labelKey: 'newDocument.docTypes.specification' },
  { code: 'FORM', labelKey: 'newDocument.docTypes.form' },
] as const;

export interface NewDocumentFormProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (response: CreateDocumentResponse) => void;
}

const MAX_UPLOAD_BYTES = 8 * 1024 * 1024; // 8 MB ceiling for the upload stub

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result as string;
      // strip the data:*;base64, prefix → just the b64 body
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export function NewDocumentForm({ isOpen, onClose, onCreated }: NewDocumentFormProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLSelectElement>(null);

  const [docTypeCode, setDocTypeCode] = useState<string>('SOP');
  const [title, setTitle]             = useState('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [templateId, setTemplateId]     = useState<string>('');
  const [file, setFile]                 = useState<File | null>(null);
  // DMS-10 GMP metadata (reviewer/approver come from the routing chain — DMS-11).
  const [language, setLanguage]                   = useState<string>('EN');
  const [category, setCategory]                   = useState<string>('');
  const [controlProfile, setControlProfile]       = useState<string>('GMP_CONTROLLED');
  const [reviewPeriodMonths, setReviewPeriodMonths] = useState<string>('24');
  const [submitting, setSubmitting]     = useState(false);
  const [serverError, setServerError]   = useState<string | null>(null);

  const depsQ      = useDepartments();
  const templatesQ = useSopTemplates(docTypeCode);
  const chainQ     = useReviewerChain(departmentId || undefined);

  // Departments tree → flat list for the dropdown, indented by depth.
  const departmentOptions = useMemo(() => {
    const all = depsQ.data?.items ?? [];
    const byParent = new Map<string | null, typeof all>();
    for (const d of all) {
      const k = d.parentId ?? null;
      if (!byParent.has(k)) byParent.set(k, [] as typeof all);
      byParent.get(k)!.push(d);
    }
    const out: { id: string; label: string; depth: number; code: string }[] = [];
    function walk(parent: string | null, depth: number) {
      const kids = (byParent.get(parent) ?? []).filter((d) => d.isActive);
      kids.sort((a, b) => a.name.localeCompare(b.name));
      for (const d of kids) {
        out.push({ id: d.departmentId, label: '—'.repeat(depth) + ' ' + d.name, depth, code: d.code });
        walk(d.departmentId, depth + 1);
      }
    }
    walk(null, 0);
    return out;
  }, [depsQ.data]);

  // Filter templates by selected doc type — server already does this, but we
  // also keep only templates targeting the chosen department or its ancestors.
  const filteredTemplates = useMemo(() => {
    const items = templatesQ.data?.items ?? [];
    if (!departmentId) return items;
    const all = depsQ.data?.items ?? [];
    const byId = new Map(all.map((d) => [d.departmentId, d] as const));
    const ancestors = new Set<string>();
    let cur: string | null = departmentId;
    while (cur) {
      ancestors.add(cur);
      cur = byId.get(cur)?.parentId ?? null;
    }
    return items.filter((tpl) => !tpl.owningDepartmentId || ancestors.has(tpl.owningDepartmentId));
  }, [templatesQ.data, departmentId, depsQ.data]);

  useEffect(() => {
    if (!isOpen) return;
    setServerError(null);
    setDocTypeCode('SOP');
    setTitle('');
    setDepartmentId('');
    setTemplateId('');
    setFile(null);
    setLanguage('EN');
    setCategory('');
    setReviewPeriodMonths('24');
    setSubmitting(false);
    setTimeout(() => firstFieldRef.current?.focus(), 0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  function onBackdropClick(e: FormEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  function onPickFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    if (!f) { setFile(null); return; }
    if (f.size > MAX_UPLOAD_BYTES) {
      setServerError(t('newDocument.errors.uploadTooLarge', { defaultValue: 'File exceeds 8 MB upload limit.' }));
      return;
    }
    setServerError(null);
    setFile(f);
  }

  function onPickTemplate(e: ChangeEvent<HTMLSelectElement>) {
    const id = e.target.value;
    setTemplateId(id);
    if (id) {
      const tpl = (templatesQ.data?.items ?? []).find((t) => t.templateId === id);
      if (tpl && !title.trim()) setTitle(tpl.title);
      if (tpl?.owningDepartmentId && !departmentId) setDepartmentId(tpl.owningDepartmentId);
    }
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setServerError(null);

    if (!title.trim() || title.trim().length < 3) {
      setServerError(t('newDocument.errors.titleTooShort', { defaultValue: 'Title must be at least 3 characters.' }));
      return;
    }
    if (!departmentId) {
      setServerError(t('newDocument.errors.deptRequired', { defaultValue: 'Department is required (department-routed review).' }));
      return;
    }

    setSubmitting(true);
    try {
      let artifactBase64: string | undefined;
      let artifactMime: string | undefined;
      let artifactFilename: string | undefined;
      if (file) {
        artifactBase64 = await fileToBase64(file);
        artifactMime   = file.type || 'application/octet-stream';
        artifactFilename = file.name;
      }
      const response = await apiFetch<CreateDocumentResponse>('/api/v1/documents/upload', {
        method: 'POST',
        body: {
          title:        title.trim(),
          docTypeCode,
          departmentId,
          templateId:   templateId || undefined,
          language,
          category:     category || undefined,
          // DMS-23 / C-14 — for GMP docs the category select IS the 6-level
          // document class; for general-controlled (corporate) docs it's a
          // corporate category (HR/Finance/…), so document_class stays null.
          documentClass: controlProfile === 'GMP_CONTROLLED' ? (category || undefined) : undefined,
          controlProfile,
          reviewPeriodMonths: reviewPeriodMonths || undefined,
          artifactBase64,
          artifactMime,
          artifactFilename,
        },
      });
      onCreated(response);
      onClose();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className={styles.backdrop} onClick={onBackdropClick} role="presentation">
      <div ref={dialogRef} className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="new-document-title" aria-describedby="new-document-desc">
        <header className={styles.header}>
          <h2 id="new-document-title" className={styles.title}>{t('newDocument.title')}</h2>
          <p id="new-document-desc" className={styles.desc}>
            {t('newDocument.subtitle', { defaultValue: 'Pick a department, optionally start from an approved template, and upload your draft.' })}
          </p>
        </header>

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          {serverError && (
            <div role="alert" className={styles.errorBanner}>{serverError}</div>
          )}

          <div className={styles.field}>
            <label htmlFor="doc_type_code" className={styles.label}>
              {t('newDocument.docTypeLabel')}<span aria-hidden="true" className={styles.required}>*</span>
            </label>
            <select
              id="doc_type_code"
              ref={firstFieldRef}
              value={docTypeCode}
              onChange={(e) => { setDocTypeCode(e.target.value); setTemplateId(''); }}
              className={styles.select}
            >
              {DOC_TYPE_CODES.map((dt) => (
                <option key={dt.code} value={dt.code}>{t(dt.labelKey)} ({dt.code})</option>
              ))}
            </select>
          </div>

          {/* DMS-23 — control profile: GMP-controlled (full Part-11 rigor) vs
              general-controlled (non-GMP, controlled + versioned but lighter).
              Chosen first because it drives the category taxonomy below (C-14). */}
          <div className={styles.field}>
            <label htmlFor="doc_control_profile" className={styles.label}>
              {t('newDocument.controlProfileLabel', { defaultValue: 'Control profile' })}
            </label>
            <select id="doc_control_profile" value={controlProfile}
              onChange={(e) => { setControlProfile(e.target.value); setCategory(''); }} className={styles.select}>
              <option value="GMP_CONTROLLED">{t('newDocument.profileGmp', { defaultValue: 'GMP-controlled (full Part-11 rigor)' })}</option>
              <option value="GENERAL_CONTROLLED">{t('newDocument.profileGeneral', { defaultValue: 'General-controlled (non-GMP — lighter)' })}</option>
            </select>
            <p className={styles.hint} style={{ fontSize: 12, color: 'var(--color-text-muted, #6B7989)', margin: '4px 0 0' }}>
              {controlProfile === 'GENERAL_CONTROLLED'
                ? t('newDocument.profileGeneralHint', { defaultValue: 'Controlled + versioned, but no GMP training trigger and a lighter review path.' })
                : t('newDocument.profileGmpHint', { defaultValue: 'Full GMP lifecycle: e-signature, periodic review, and training on effective date.' })}
            </p>
          </div>

          {/* DMS-10 / C-14 — for GMP docs this is the 6-level document class; for
              general-controlled (corporate) docs it's a corporate category. */}
          <div className={styles.field}>
            <label htmlFor="doc_category" className={styles.label}>
              {controlProfile === 'GENERAL_CONTROLLED'
                ? t('newDocument.corporateCategoryLabel', { defaultValue: 'Corporate category' })
                : t('newDocument.categoryLabel', { defaultValue: 'GMP category (classification level)' })}
            </label>
            <select id="doc_category" value={category} onChange={(e) => setCategory(e.target.value)} className={styles.select}>
              <option value="">{t('newDocument.categoryNone', { defaultValue: '— Select —' })}</option>
              {controlProfile === 'GENERAL_CONTROLLED' ? (
                <>
                  <option value="HR">Human Resources</option>
                  <option value="FINANCE">Finance</option>
                  <option value="PROCUREMENT">Procurement</option>
                  <option value="LEGAL">Legal &amp; Compliance</option>
                  <option value="IT">IT &amp; Systems</option>
                  <option value="EHS">Environment, Health &amp; Safety</option>
                  <option value="FACILITIES">Facilities &amp; Admin</option>
                  <option value="GENERAL">General / Corporate</option>
                </>
              ) : (
                <>
                  <option value="QUALITY_MANUAL">Quality Manual (L1)</option>
                  <option value="POLICY">Policy (L2)</option>
                  <option value="SOP">SOP (L3)</option>
                  <option value="WORK_INSTRUCTION">Work Instruction (L4)</option>
                  <option value="FORM">Form (L5)</option>
                  <option value="RECORD">Record (L6)</option>
                </>
              )}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="doc_review_period" className={styles.label}>
              {t('newDocument.reviewPeriodLabel', { defaultValue: 'Periodic review frequency' })}
            </label>
            <select id="doc_review_period" value={reviewPeriodMonths} onChange={(e) => setReviewPeriodMonths(e.target.value)} className={styles.select}>
              <option value="12">{t('newDocument.review12', { defaultValue: 'Every 12 months' })}</option>
              <option value="24">{t('newDocument.review24', { defaultValue: 'Every 24 months' })}</option>
              <option value="36">{t('newDocument.review36', { defaultValue: 'Every 36 months' })}</option>
              <option value="">{t('newDocument.reviewNone', { defaultValue: 'No periodic review' })}</option>
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="doc_language" className={styles.label}>
              {t('newDocument.languageLabel', { defaultValue: 'Language' })}
            </label>
            <select id="doc_language" value={language} onChange={(e) => setLanguage(e.target.value)} className={styles.select}>
              <option value="EN">{t('newDocument.langEn', { defaultValue: 'English' })}</option>
              <option value="AR">{t('newDocument.langAr', { defaultValue: 'Arabic' })}</option>
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="departmentId" className={styles.label}>
              {t('newDocument.departmentLabel', { defaultValue: 'Department' })}
              <span aria-hidden="true" className={styles.required}>*</span>
            </label>
            <select
              id="departmentId"
              value={departmentId}
              onChange={(e) => { setDepartmentId(e.target.value); setTemplateId(''); }}
              className={styles.select}
              aria-invalid={!departmentId ? 'true' : 'false'}
            >
              <option value="">{t('newDocument.departmentPlaceholder', { defaultValue: '— Pick a department —' })}</option>
              {departmentOptions.map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
            {departmentId && chainQ.data?.chain && (
              <div className={styles.helperText} aria-live="polite">
                {t('newDocument.routeHelper', {
                  defaultValue: 'On submit for review, this doc will route through: ',
                })}
                <strong>
                  {chainQ.data.chain.map((c, i) =>
                    `${c.userName}${c.positionKind === 'QA_MANAGER' ? ' (QA)' : ''}` + (i < chainQ.data!.chain.length - 1 ? ' → ' : ''),
                  ).join('')}
                </strong>
              </div>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="templateId" className={styles.label}>
              {t('newDocument.templateLabel', { defaultValue: 'Start from approved template (optional)' })}
            </label>
            <select
              id="templateId"
              value={templateId}
              onChange={onPickTemplate}
              className={styles.select}
              disabled={templatesQ.isLoading || filteredTemplates.length === 0}
            >
              <option value="">
                {filteredTemplates.length === 0
                  ? t('newDocument.templateEmpty', { defaultValue: '— No approved template available —' })
                  : t('newDocument.templatePlaceholder', { defaultValue: '— No template (blank document) —' })}
              </option>
              {filteredTemplates.map((tpl) => (
                <option key={tpl.templateId} value={tpl.templateId}>
                  {tpl.code} — {tpl.title} (v{tpl.versionMajor}.{tpl.versionMinor})
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="title" className={styles.label}>
              {t('newDocument.titleLabel')}<span aria-hidden="true" className={styles.required}>*</span>
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={styles.input}
              maxLength={200}
              placeholder={t('newDocument.titlePlaceholder')}
              aria-invalid={!title.trim() || title.trim().length < 3 ? 'true' : 'false'}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="upload" className={styles.label}>
              {t('newDocument.uploadLabel', { defaultValue: 'Upload draft (optional, ≤ 8 MB)' })}
            </label>
            <input
              id="upload"
              type="file"
              accept=".doc,.docx,.pdf,.odt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={onPickFile}
              className={styles.input}
            />
            {file && (
              <div className={styles.helperText}>
                <strong>{file.name}</strong> · {Math.round(file.size / 1024)} KB · {file.type || 'application/octet-stream'}
              </div>
            )}
          </div>

          <footer className={styles.footer}>
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" isLoading={submitting}>
              {t('newDocument.submit')}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
